// Centralized SignalR hub client used across the site.
// This module creates a single HubConnection to '/hubs/notifications' and exposes a small API:
// - notificationHub.startPromise: Promise that resolves when connection has started
// - notificationHub.on(event, handler): register a handler for hub messages
// - notificationHub.invoke(method, ...args): invoke a hub method
// - notificationHub.addEventListener(name, fn): listen to lifecycle events: 'connected','reconnecting','reconnected','closed'
// The script assumes `signalR` is available globally.
(function () {
    'use strict';
    // Ensure SignalR script is available. If missing, attempt to load from CDN and wait.
    async function ensureSignalR() {
        if (typeof signalR !== 'undefined') return true;
        try {
            const cdn = 'https://cdn.jsdelivr.net/npm/@microsoft/signalr@7.0.5/dist/browser/signalr.min.js';
            await new Promise((resolve, reject) => {
                const s = document.createElement('script');
                s.src = cdn;
                s.async = true;
                s.onload = () => resolve(true);
                s.onerror = () => reject(new Error('Failed to load SignalR from CDN'));
                document.head.appendChild(s);
            });
            return typeof signalR !== 'undefined';
        } catch (e) {
            console.warn('hub-client: signalR not found and CDN load failed', e);
            return false;
        }
    }

    // If already initialized, do nothing and keep existing API
    if (window.notificationHub && window.__notificationHubInit) {
        return;
    }

    // mark as initializing so hot-reload / duplicate script doesn't recreate connection
    window.__notificationHubInit = true;

  const evtTarget = new EventTarget();
  let connection = null;
  let _startResolve, _startReject;
  const startPromise = new Promise((resolve, reject) => { _startResolve = resolve; _startReject = reject; });

    window.notificationHub = {
        startPromise: startPromise,
      on: function (eventName, handler) {
        evtTarget.addEventListener(eventName, handler);
      },
      invoke: function () {
        return Promise.reject(new Error('not ready'));
      },
      addEventListener: function (name, fn) { evtTarget.addEventListener(name, fn); },
      getConnectionId: function () { return null; },
      _connection: function () { return null; }
    };

    // Start connection eagerly (async function will resolve the placeholder when ready)
    (async function () {
      const ok = await ensureSignalR();
      if (!ok) {
        window.notificationHub = null;
        _startReject && _startReject(new Error('SignalR not available'));
        return;
      }
      // Build an absolute URL using the current page origin to avoid mixed-scheme/host/port issues.
      const base = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
      const hubUrl = base + '/hubs/notifications';
      try { console.debug('hub-client: attempting to connect to SignalR hub at', hubUrl); } catch (e) { }

      connection =
        new signalR.HubConnectionBuilder().withUrl(hubUrl).withAutomaticReconnect().build();

        connection.onreconnecting(error => {
            try { evtTarget.dispatchEvent(new CustomEvent('reconnecting', { detail: error })); } catch { }
        });
        connection.onreconnected(connectionId => {
            try { evtTarget.dispatchEvent(new CustomEvent('reconnected', { detail: connectionId })); } catch { }
        });
        connection.onclose(error => {
            try { evtTarget.dispatchEvent(new CustomEvent('closed', { detail: error })); } catch { }
        });
        try {
            await connection.start();
            const setConnected = (conn) => {
                try { evtTarget.dispatchEvent(new CustomEvent('connected', { detail: conn.connectionId })); } catch { }
                console.info('hub-client: SignalR connected', conn.connectionId);
                _startResolve(conn);
                window.notificationHub.startPromise = Promise.resolve(conn);
                window.notificationHub.on = function (eventName, handler) { try { conn.on(eventName, handler); } catch (e) { console.warn('hub-client.on failed', e); }};
                window.notificationHub.invoke = function (methodName, ...args) { return conn.invoke(methodName, ...args); };
                window.notificationHub.getConnectionId = function () { return conn ? conn.connectionId : null; };
                window.notificationHub._connection = function () { return conn; };
            };

            setConnected(connection);
            return connection;
        }
        catch (e) {
          console.warn('hub-client: failed to start, attempting long-polling fallback then background retry', e);

          // Try a one-time long-polling fallback which can help through proxies/dev-tunnels
          try {
            const lpConn = new signalR.HubConnectionBuilder()
              .withUrl(hubUrl, { transport: signalR.HttpTransportType.LongPolling })
              .withAutomaticReconnect()
              .build();
            await lpConn.start();
            setConnected(lpConn);
            connection = lpConn;
            console.info('hub-client: SignalR connected via LongPolling', connection.connectionId);
            return connection;
          }
          catch (lpErr) {
            console.warn('hub-client: long-polling fallback failed', lpErr);
          }

          // If fallback didn't work, continue background retry attempts using original connection
          (async function retry() {
            while (true) {
              try {
                await new Promise(r => setTimeout(r, 3000));
                await connection.start();
                try { evtTarget.dispatchEvent(new CustomEvent('connected', { detail: connection.connectionId })); } catch { }
                console.info('hub-client: reconnected', connection.connectionId);
                _startResolve(connection);
                window.notificationHub.startPromise = Promise.resolve(connection);
                window.notificationHub.on = function (eventName, handler) {
                  try {
                    connection.on(eventName, handler);
                  } catch (e) {
                    console.warn('hub-client.on failed', e);
                  }
                };
                window.notificationHub.invoke = function (methodName, ...args) { return connection.invoke(methodName, ...args); };
                window.notificationHub.getConnectionId = function () { return connection ? connection.connectionId : null; };
                window.notificationHub._connection = function () { return connection; };
                break;
              } catch (err) {
                console.warn('hub-client: retry start failed', err);
              }
            }
          })();
          return connection;
      }
    })();
})();
